import {
  DEFAULT_ICON_SIZE,
  FALLBACK_ICON_SIZE,
  FALLBACK_ICON_FONT_SIZE,
  FALLBACK_ICON_BACKGROUND,
  FALLBACK_ICON_TEXT_COLOR,
  DEFAULT_FILL_COLOR,
  DEFAULT_STROKE_COLOR,
  FULL_BORDER_RADIUS,
  BLOCK_DISPLAY,
  SVG_NAMESPACE,
  EXTERNAL_SHADOW_FILTERS,
  SELECTION_HIGHLIGHT_FILTER,
} from './markerConstants';

export interface IconConfig {
  url?: string;
  scaledSize?: { width: number; height: number };
  size?: { width: number; height: number };
  path?: string;
  scale?: number;
  fillColor?: string;
  fillOpacity?: number;
  strokeColor?: string;
  strokeWeight?: number;
  strokeOpacity?: number;
  rotation?: number;
  anchor?: { x: number; y: number };
  windowPath?: string;
}

/**
 * Creates an HTML or SVG element from an icon configuration.
 * Handles string URLs, icon objects with URLs, SVG path data, and provides fallback rendering.
 */
export function createIconElement(
  icon: string | IconConfig | null | undefined,
  offsetZ?: number,
): HTMLElement | SVGElement | null {
  if (!icon) return null;

  if (typeof icon === 'string') {
    const img = document.createElement('img');
    img.src = icon;
    img.style.display = BLOCK_DISPLAY;
    if (offsetZ) {
      img.style.position = 'absolute';
      img.style.zIndex = offsetZ.toString();
    }
    return img;
  }

  if (icon.url) {
    const img = document.createElement('img');
    img.src = icon.url;
    img.style.display = BLOCK_DISPLAY;

    if (offsetZ) {
      img.style.position = 'absolute';
      img.style.zIndex = offsetZ.toString();
    }

    if (icon.scaledSize) {
      img.width = icon.scaledSize.width;
      img.height = icon.scaledSize.height;
    } else if (icon.size) {
      img.width = icon.size.width;
      img.height = icon.size.height;
    }

    if (icon.anchor) {
      img.style.transform = `translate(-${icon.anchor.x}px, -${icon.anchor.y}px)`;
    }

    return img;
  }

  if (icon.path) {
    const svg = document.createElementNS(SVG_NAMESPACE, 'svg');
    const scale = icon.scale || 1;
    const size = DEFAULT_ICON_SIZE * scale;

    svg.setAttribute('width', size.toString());
    svg.setAttribute('height', size.toString());
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.style.display = BLOCK_DISPLAY;

    if (offsetZ) {
      svg.style.position = 'absolute';
      svg.style.zIndex = offsetZ.toString();
    }

    if (icon.anchor) {
      svg.style.transform = `translate(-${icon.anchor.x}px, -${icon.anchor.y}px)`;
    }

    const path = document.createElementNS(SVG_NAMESPACE, 'path');
    path.setAttribute('d', icon.path);
    path.setAttribute('fill', icon.fillColor || DEFAULT_FILL_COLOR);
    path.setAttribute('fill-opacity', (icon.fillOpacity || 1).toString());
    path.setAttribute('stroke', icon.strokeColor || DEFAULT_STROKE_COLOR);
    path.setAttribute('stroke-width', (icon.strokeWeight || 0).toString());

    if (icon.strokeOpacity != null) {
      path.setAttribute('stroke-opacity', icon.strokeOpacity.toString());
    }

    if (icon.rotation) {
      path.setAttribute('transform', `rotate(${icon.rotation} ${size / 2} ${size / 2})`);
    }

    svg.appendChild(path);

    if (icon.windowPath) {
      const windowPathEl = document.createElementNS(SVG_NAMESPACE, 'path');
      windowPathEl.setAttribute('d', icon.windowPath);
      windowPathEl.setAttribute('fill', '#4A4A4A');
      svg.appendChild(windowPathEl);
    }

    return svg;
  }

  // Fallback to default icon
  const fallback = document.createElement('div');
  fallback.textContent = 'T';
  fallback.style.width = `${FALLBACK_ICON_SIZE}px`;
  fallback.style.height = `${FALLBACK_ICON_SIZE}px`;
  fallback.style.backgroundColor = FALLBACK_ICON_BACKGROUND;
  fallback.style.color = FALLBACK_ICON_TEXT_COLOR;
  fallback.style.borderRadius = FULL_BORDER_RADIUS;
  fallback.style.display = 'flex';
  fallback.style.alignItems = 'center';
  fallback.style.justifyContent = 'center';
  fallback.style.fontSize = `${FALLBACK_ICON_FONT_SIZE}px`;
  fallback.style.fontWeight = 'bold';

  if (offsetZ) {
    fallback.style.position = 'absolute';
    fallback.style.zIndex = offsetZ.toString();
  }

  return fallback;
}

/**
 * Applies shadow filters to an element for external markers.
 */
export function applyExternalShadowFilters(
  element: HTMLElement | SVGElement,
  shadowFilters: string[],
  isSelected?: boolean,
  selectionFilter?: string,
): void {
  element.style.filter = shadowFilters.join(' ');

  if (isSelected && selectionFilter) {
    element.style.filter += ` ${selectionFilter}`;
  }
}

export function createTruckIcon(
  icon: any,
  applyShadow = false,
  isSelected: boolean = false,
): HTMLElement | SVGElement {
  let iconElement = createIconElement(icon, icon?.scale || 1);
  if (!iconElement) {
    iconElement = document.createElement('div');
    iconElement.textContent = 'T';
    iconElement.style.width = '24px';
    iconElement.style.height = '24px';
    iconElement.style.backgroundColor = '#f0f0f0';
    iconElement.style.color = '#333';
    iconElement.style.borderRadius = '50%';
    iconElement.style.display = 'flex';
    iconElement.style.alignItems = 'center';
    iconElement.style.justifyContent = 'center';
    iconElement.style.fontSize = '12px';
    iconElement.style.fontWeight = 'bold';
  }

  if (applyShadow) {
    applyExternalShadowFilters(
      iconElement,
      EXTERNAL_SHADOW_FILTERS,
      isSelected,
      SELECTION_HIGHLIGHT_FILTER,
    );
  }

  return iconElement;
}
